import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Play,
  Layers,
  Cpu,
  Crosshair,
  X,
  Save,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Input,
  KeyValueGrid,
  MetricCard,
  Select,
  Tabs,
  type TabItem,
} from '@/components';
import { useData, useUI } from '@/data/stores';
import {
  runCascade,
  nMinus1,
  nMinus2,
  minimumCut,
  type ContingencyResult,
} from '@/domain';
import type { Asset, ScenarioInput, ScenarioMode, AttackerProfile } from '@/types';
import { CascadeViz } from './CascadeViz';
import { fmtCompact, fmtCurrency, fmtNumber } from '@/lib/format';
import './scenarios.css';

export function ScenariosView() {
  const [params] = useSearchParams();
  const { assets, edges, scenarios, addScenario } = useData();
  const setScenarioOverlay = useUI((s) => s.setScenarioOverlay);
  const clearScenarioOverlay = useUI((s) => s.clearScenarioOverlay);
  const notify = useUI((s) => s.notifyAction);

  const [selectedSeeds, setSelectedSeeds] = useState<string[]>([]);
  const [name, setName] = useState('Untitled scenario');
  const [mode, setMode] = useState<ScenarioMode>('failure');
  const [attacker, setAttacker] = useState<AttackerProfile>('nation_state');
  const [duration, setDuration] = useState<string>('72');
  const [activeTab, setActiveTab] = useState('builder');

  // Initialise from query string ?seed=
  useEffect(() => {
    const seed = params.get('seed');
    if (seed) {
      const ids = seed.split(',').map((s) => s.trim()).filter(Boolean);
      setSelectedSeeds(ids);
      const labels = ids
        .map((id) => assets.find((a) => a.asset_id === id)?.name ?? id)
        .join(' + ');
      if (labels) setName(`Failure: ${labels}`);
    }
  }, [params, assets]);

  const result = useMemo(() => {
    if (selectedSeeds.length === 0) return null;
    const scenario: ScenarioInput = {
      scenario_id: `tmp-${Date.now()}`,
      name,
      mode,
      attacker_profile: mode.startsWith('attack') ? attacker : undefined,
      initial_failed_assets: selectedSeeds,
      duration_hours: Number(duration),
      created_at: new Date().toISOString(),
    };
    return runCascade(scenario, assets, edges);
  }, [selectedSeeds, name, mode, attacker, duration, assets, edges]);

  // Sync overlay onto map
  useEffect(() => {
    if (result) {
      setScenarioOverlay(result.affected.map((n) => n.asset_id));
    }
    return () => clearScenarioOverlay();
  }, [result, setScenarioOverlay, clearScenarioOverlay]);

  const builderTab: TabItem = {
    key: 'builder',
    label: 'Scenario Builder',
    content: (
      <div className="iris-scn-grid">
        <Card
          title="Initial conditions"
          subtitle="Select one or more assets to fail and choose a mode"
        >
          <div className="iris-scn-form">
            <Input
              label="Scenario name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Select
              label="Mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as ScenarioMode)}
              options={[
                { value: 'failure', label: 'Failure (any cause)' },
                { value: 'attack_physical', label: 'Physical attack' },
                { value: 'attack_cyber', label: 'Cyber attack' },
                { value: 'attack_cyber_physical', label: 'Cyber-physical attack' },
                { value: 'supply_chain', label: 'Supply chain disruption' },
                { value: 'weather', label: 'Weather event' },
                { value: 'demand_shock', label: 'Demand shock' },
                { value: 'geopolitical', label: 'Geopolitical event' },
              ]}
            />
            {mode.startsWith('attack') && (
              <Select
                label="Attacker profile"
                value={attacker}
                onChange={(e) => setAttacker(e.target.value as AttackerProfile)}
                options={[
                  { value: 'nation_state', label: 'Nation-state' },
                  { value: 'sophisticated_criminal', label: 'Sophisticated criminal' },
                  { value: 'insider', label: 'Insider' },
                  { value: 'opportunistic', label: 'Opportunistic' },
                  { value: 'hacktivist', label: 'Hacktivist' },
                ]}
              />
            )}
            <Input
              label="Duration (hours)"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              type="number"
              min={1}
              max={8760}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="caption" style={{ marginBottom: 4 }}>
              Failed assets ({selectedSeeds.length})
            </div>
            <div className="iris-scn-seedlist">
              {selectedSeeds.length === 0 && (
                <span className="caption">Pick assets below to seed the scenario.</span>
              )}
              {selectedSeeds.map((id) => {
                const a = assets.find((x) => x.asset_id === id);
                return (
                  <span key={id} className="iris-scn-chip">
                    {a?.name ?? id}
                    <button
                      onClick={() =>
                        setSelectedSeeds((s) => s.filter((x) => x !== id))
                      }
                      aria-label="Remove"
                    >
                      <X size={10} strokeWidth={1.5} />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          <div className="iris-scn-actions">
            <Button
              variant="primary"
              icon={<Play size={14} strokeWidth={1.5} />}
              disabled={selectedSeeds.length === 0}
              onClick={() =>
                notify({
                  kind: 'success',
                  title: 'Simulation run complete',
                  body: result
                    ? `${result.total_assets_impacted} assets impacted; map overlay updated.`
                    : 'Select one or more seed assets to run a cascade simulation.',
                })
              }
            >
              Run simulation
            </Button>
            <Button
              icon={<Save size={14} strokeWidth={1.5} />}
              disabled={selectedSeeds.length === 0}
              onClick={() => {
                const sc: ScenarioInput = {
                  scenario_id: `SCN-${Date.now()}`,
                  name,
                  mode,
                  attacker_profile: mode.startsWith('attack') ? attacker : undefined,
                  initial_failed_assets: selectedSeeds,
                  duration_hours: Number(duration),
                  created_at: new Date().toISOString(),
                };
                addScenario(sc);
                notify({
                  kind: 'success',
                  title: 'Scenario saved',
                  body: `${sc.name} added to Saved Scenarios.`,
                });
              }}
            >
              Save
            </Button>
          </div>
        </Card>

        <Card title="Pick seed assets" subtitle="Click to add. Filter by criticality.">
          <SeedPicker
            assets={assets}
            selected={selectedSeeds}
            onToggle={(id) =>
              setSelectedSeeds((s) =>
                s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
              )
            }
          />
        </Card>

        {result && (
          <>
            <Card
              title={`Cascade impact - ${result.total_assets_impacted} assets`}
              subtitle={`Recovery estimate ${result.recovery_hours_estimate}h · economic ${fmtCurrency(
                (result.economic_impact_eur_m ?? 0) * 1_000_000,
              )}`}
            >
              <div className="iris-scn-result-metrics">
                <MetricCard
                  label="Affected"
                  value={result.total_assets_impacted}
                  caption="across full graph"
                />
                <MetricCard
                  label="Population impacted"
                  value={fmtCompact(result.population_impacted_estimate ?? 0)}
                  status="var(--status-degraded)"
                />
                <MetricCard
                  label="Economic impact"
                  value={fmtCurrency((result.economic_impact_eur_m ?? 0) * 1_000_000)}
                  status="var(--status-critical)"
                />
                <MetricCard
                  label="Recovery"
                  value={result.recovery_hours_estimate ?? 0}
                  unit="h"
                />
                <MetricCard
                  label="Loss p10"
                  value={`${result.confidence_intervals?.p10 ?? 0}%`}
                />
                <MetricCard
                  label="Loss p50"
                  value={`${result.confidence_intervals?.p50 ?? 0}%`}
                />
                <MetricCard
                  label="Loss p90"
                  value={`${result.confidence_intervals?.p90 ?? 0}%`}
                />
              </div>
            </Card>
            <Card title="Cascade tree (time-ordered)">
              <CascadeViz preview={result} assets={assets} />
            </Card>
          </>
        )}
      </div>
    ),
  };

  const contingencyTab: TabItem = {
    key: 'contingency',
    label: 'N-k Contingency',
    content: <ContingencyPanel assets={assets} edges={edges} />,
  };

  const adversarialTab: TabItem = {
    key: 'adversarial',
    label: 'Adversarial Min-Cut',
    content: <AdversarialPanel assets={assets} edges={edges} />,
  };

  const savedTab: TabItem = {
    key: 'saved',
    label: 'Saved Scenarios',
    badge: <span>{scenarios.length}</span>,
    content: (
      <div className="iris-scn-saved">
        {scenarios.map((s) => (
          <Card
            key={s.scenario_id}
            title={s.name}
            subtitle={`${s.mode} · ${s.initial_failed_assets.length} seed asset(s)${
              s.attacker_profile ? ` · ${s.attacker_profile}` : ''
            }`}
            actions={
              <Button
                size="sm"
                onClick={() => {
                  setSelectedSeeds(s.initial_failed_assets);
                  setName(s.name);
                  setMode(s.mode);
                  if (s.attacker_profile) setAttacker(s.attacker_profile);
                  if (s.duration_hours) setDuration(String(s.duration_hours));
                  setActiveTab('builder');
                  notify({
                    title: 'Scenario loaded',
                    body: `${s.initial_failed_assets.length} seed asset(s) loaded into the builder.`,
                  });
                }}
              >
                Load
              </Button>
            }
          >
            {s.description}
          </Card>
        ))}
      </div>
    ),
  };

  return (
    <div className="iris-page">
      <header className="iris-page-header">
        <div>
          <h1>Scenario Modelling & Simulation</h1>
          <p className="caption">
            Layer 4 - Probabilistic projections, adversarial simulation, and N-k contingency
            across the full interdependency graph.
          </p>
        </div>
        <div className="iris-page-actions">
          <Button
            icon={<Plus size={14} strokeWidth={1.5} />}
            onClick={() => {
              setSelectedSeeds([]);
              setName('Untitled scenario');
              setMode('failure');
              setAttacker('nation_state');
              setDuration('72');
              setActiveTab('builder');
              notify({ title: 'New scenario ready', body: 'Builder reset to default inputs.' });
            }}
          >
            New scenario
          </Button>
        </div>
      </header>

      <Tabs
        items={[builderTab, contingencyTab, adversarialTab, savedTab]}
        activeKey={activeTab}
        onChange={setActiveTab}
      />
    </div>
  );
}

function SeedPicker({
  assets,
  selected,
  onToggle,
}: {
  assets: Asset[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  const [crit, setCrit] = useState<string>('5');
  const [country, setCountry] = useState<string>('');
  const filtered = useMemo(() => {
    return assets
      .filter((a) => (crit ? a.criticality_rating === Number(crit) : true))
      .filter((a) => (country ? a.country === country : true))
      .slice(0, 60);
  }, [assets, crit, country]);
  const countries = Array.from(new Set(assets.map((a) => a.country))).sort();
  return (
    <>
      <div className="iris-seed-filters">
        <Select
          label="Criticality"
          value={crit}
          onChange={(e) => setCrit(e.target.value)}
          options={[
            { value: '', label: 'All' },
            { value: '5', label: '5' },
            { value: '4', label: '4' },
            { value: '3', label: '3' },
            { value: '2', label: '2' },
            { value: '1', label: '1' },
          ]}
        />
        <Select
          label="Country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          options={[
            { value: '', label: 'All' },
            ...countries.map((c) => ({ value: c, label: c })),
          ]}
        />
      </div>
      <div className="iris-seed-list">
        {filtered.map((a) => (
          <button
            key={a.asset_id}
            className={`iris-seed-row ${selected.includes(a.asset_id) ? 'on' : ''}`}
            onClick={() => onToggle(a.asset_id)}
          >
            <span>{a.name}</span>
            <span className="caption mono">
              {a.country} · CRIT {a.criticality_rating}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

function ContingencyPanel({ assets, edges }: { assets: Asset[]; edges: ReturnType<typeof useData.getState>['edges'] }) {
  const [running, setRunning] = useState<'n1' | 'n2' | 'idle'>('idle');
  const [n1, setN1] = useState<ContingencyResult[]>([]);
  const [n2, setN2] = useState<ContingencyResult[]>([]);

  return (
    <div className="iris-scn-grid">
      <Card
        title="N-1 contingency"
        subtitle="Enumerate every single-asset failure and rank impact"
        actions={
          <Button
            size="sm"
            icon={<Cpu size={12} strokeWidth={1.5} />}
            disabled={running !== 'idle'}
            onClick={() => {
              setRunning('n1');
              setTimeout(() => {
                setN1(
                  nMinus1(assets, edges, (a) => a.criticality_rating >= 3).slice(0, 25),
                );
                setRunning('idle');
              }, 0);
            }}
          >
            {running === 'n1' ? 'Running…' : 'Run N-1'}
          </Button>
        }
      >
        <ContingencyTable rows={n1} assets={assets} />
      </Card>
      <Card
        title="N-2 contingency"
        subtitle="Pairs from top-12 critical assets"
        actions={
          <Button
            size="sm"
            icon={<Layers size={12} strokeWidth={1.5} />}
            disabled={running !== 'idle'}
            onClick={() => {
              setRunning('n2');
              setTimeout(() => {
                setN2(nMinus2(assets, edges).slice(0, 25));
                setRunning('idle');
              }, 0);
            }}
          >
            {running === 'n2' ? 'Running…' : 'Run N-2'}
          </Button>
        }
      >
        <ContingencyTable rows={n2} assets={assets} />
      </Card>
    </div>
  );
}

function ContingencyTable({ rows, assets }: { rows: ContingencyResult[]; assets: Asset[] }) {
  if (rows.length === 0)
    return <div className="caption" style={{ padding: 8 }}>No results yet.</div>;
  return (
    <table className="iris-table" style={{ marginTop: 4 }}>
      <thead>
        <tr>
          <th>Failed asset(s)</th>
          <th style={{ textAlign: 'right' }}>Impacted</th>
          <th style={{ textAlign: 'right' }}>Population</th>
          <th style={{ textAlign: 'right' }}>Economic (€)</th>
          <th style={{ textAlign: 'right' }}>Loss p50</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>
              {r.failed_assets
                .map((id) => assets.find((a) => a.asset_id === id)?.name ?? id)
                .join(' + ')}
            </td>
            <td style={{ textAlign: 'right' }} className="mono">
              {r.total_assets_impacted}
            </td>
            <td style={{ textAlign: 'right' }} className="mono">
              {fmtCompact(r.population_impacted)}
            </td>
            <td style={{ textAlign: 'right' }} className="mono">
              {fmtNumber(r.economic_impact_eur_m, { maximumFractionDigits: 0 })}M
            </td>
            <td style={{ textAlign: 'right' }} className="mono">
              {r.loss_p50}%
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AdversarialPanel({ assets, edges }: { assets: Asset[]; edges: ReturnType<typeof useData.getState>['edges'] }) {
  const [target, setTarget] = useState('25');
  const [maxSize, setMaxSize] = useState('5');
  const [profile, setProfile] = useState<AttackerProfile>('nation_state');
  const [result, setResult] = useState<ContingencyResult | null>(null);
  const [running, setRunning] = useState(false);
  return (
    <div className="iris-scn-grid">
      <Card
        title="Adversarial minimum-cut analysis"
        subtitle="Greedy heuristic - what is the smallest set of assets whose simultaneous failure achieves a target impact?"
      >
        <div className="iris-scn-form">
          <Input
            label="Target impacted assets"
            type="number"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
          <Input
            label="Max set size"
            type="number"
            value={maxSize}
            onChange={(e) => setMaxSize(e.target.value)}
          />
          <Select
            label="Attacker"
            value={profile}
            onChange={(e) => setProfile(e.target.value as AttackerProfile)}
            options={[
              { value: 'nation_state', label: 'Nation-state' },
              { value: 'sophisticated_criminal', label: 'Sophisticated criminal' },
              { value: 'insider', label: 'Insider' },
              { value: 'hacktivist', label: 'Hacktivist' },
            ]}
          />
        </div>
        <div className="iris-scn-actions">
          <Button
            variant="primary"
            icon={<Crosshair size={14} strokeWidth={1.5} />}
            disabled={running}
            onClick={() => {
              setRunning(true);
              setTimeout(() => {
                const filter =
                  profile === 'insider'
                    ? (a: Asset) =>
                        a.criticality_rating >= 4 &&
                        ['energy_transmission', 'energy_generation', 'data_centres'].includes(a.sector)
                    : profile === 'hacktivist'
                      ? (a: Asset) => a.criticality_rating >= 3
                      : (a: Asset) => a.criticality_rating >= 4;
                const r = minimumCut(
                  assets,
                  edges,
                  Number(target),
                  Number(maxSize),
                  filter,
                );
                setResult(r);
                setRunning(false);
              }, 0);
            }}
          >
            {running ? 'Computing…' : 'Compute min-cut'}
          </Button>
        </div>
      </Card>

      {result && (
        <Card title="Min-cut result" accent="var(--status-critical)">
          <KeyValueGrid
            cols={2}
            items={[
              {
                label: 'Set size',
                value: `${result.failed_assets.length}`,
                mono: true,
              },
              { label: 'Impacted assets', value: result.total_assets_impacted, mono: true },
              { label: 'Population', value: fmtCompact(result.population_impacted), mono: true },
              {
                label: 'Economic',
                value: fmtCurrency(result.economic_impact_eur_m * 1_000_000),
                mono: true,
              },
              { label: 'Loss p50', value: `${result.loss_p50}%`, mono: true },
            ]}
          />
          <div className="iris-scn-mincut">
            <div className="caption" style={{ margin: '8px 0 4px' }}>
              Critical assets identified
            </div>
            <ol>
              {result.failed_assets.map((id, i) => {
                const a = assets.find((x) => x.asset_id === id);
                return (
                  <li key={id}>
                    <span className="mono iris-scn-mincut-num">{i + 1}.</span>
                    <span>{a?.name ?? id}</span>
                    <Badge color="var(--status-critical)">CRIT {a?.criticality_rating}</Badge>
                  </li>
                );
              })}
            </ol>
          </div>
        </Card>
      )}
    </div>
  );
}
